import { formatUsd, languageByCode, measureLine, type LanguageDepth } from "@veyso/core";
import type { AvailableLanguage, LanguageStatus, LanguageView, LanguagesView } from "../api.js";

/**
 * The languages page's arithmetic and words, kept apart from its components so
 * the tests at the root can run them without React.
 */

/** Statuses with a job that will move the language on by itself; the page polls while any is one. */
const IN_PROGRESS: readonly LanguageStatus[] = ["translating", "voicing", "revoicing"];

export const isInProgress = (status: LanguageStatus) => IN_PROGRESS.includes(status);

export function anyInProgress(view: Pick<LanguagesView, "languages"> | null): boolean {
  return view?.languages.some((language) => isInProgress(language.status)) ?? false;
}

export type StatusTone = "ok" | "warn" | "busy" | "bad";

/**
 * What a language's badge says.
 *
 * Out of date wins over the status it finished in, because that is what the
 * customer has to act on; a language still being made says so instead, since
 * there is nothing to do until it stops, and a failed one says it failed.
 */
export function statusLabel(language: Pick<LanguageView, "status" | "outOfDate">): { text: string; tone: StatusTone } {
  if (language.status === "failed") return { text: "Couldn't be made — nothing was charged", tone: "bad" };
  if (isInProgress(language.status)) {
    const text =
      language.status === "translating"
        ? "Translating…"
        : language.status === "voicing"
          ? "Voicing…"
          : "Voicing your changes…";
    return { text, tone: "busy" };
  }
  if (language.outOfDate) return { text: "Out of date — the English changed", tone: "warn" };
  if (language.status === "review") return { text: "Script ready — check it", tone: "warn" };
  return { text: "Ready", tone: "ok" };
}

/** What one language costs at a depth: a voice-only language is the same price either way. */
export function priceFor(
  available: Pick<AvailableLanguage, "priceUsd" | "videoPriceUsd">,
  depth: LanguageDepth,
): number {
  return depth === "video" ? available.videoPriceUsd : available.priceUsd;
}

/** The chosen languages' quotes at a depth, added up to the cent. */
export function totalPrice(
  available: readonly AvailableLanguage[],
  chosen: readonly string[],
  depth: LanguageDepth,
): number {
  const cents = chosen.reduce((sum, code) => {
    const language = available.find((one) => one.code === code);
    return sum + (language ? Math.round(priceFor(language, depth) * 100) : 0);
  }, 0);
  return cents / 100;
}

/** Which of the chosen languages keep the English picture when the whole video is asked for, in words. */
export function voiceOnlyNote(
  chosen: readonly Pick<AvailableLanguage, "nativeName" | "wholeVideo">[],
  depth: LanguageDepth,
) {
  if (depth !== "video") return null;
  const names = chosen.filter((one) => !one.wholeVideo).map((one) => one.nativeName);
  if (names.length === 0) return null;
  if (names.length === 1) return `${names[0]} gets the voice and subtitles only. Its video stays in English.`;
  const head = names.slice(0, -1).join(", ");
  return `${head} and ${names[names.length - 1]} get the voice and subtitles only. Their video stays in English.`;
}

/** The languages the editor can switch its preview to: finished ones, ones to check, and ones out of date. */
export function switchable(view: Pick<LanguagesView, "languages"> | null): LanguageView[] {
  return (view?.languages ?? []).filter((one) => one.status === "ready" || one.status === "review");
}

/**
 * The request bodies for starting a language, kept pure so the rules are tested:
 * a demo says its depth, a launch version says its version and never a depth
 * (its languages are voice and subtitles).
 */
export function makeBody(chosen: string[], review: boolean, depth: LanguageDepth, version?: string) {
  // A launch version takes a depth too: the whole video, where its picture can be drawn again.
  return version ? { languages: chosen, review, version, depth } : { languages: chosen, review, depth };
}

/** "Try again" makes the failed language again at the depth its row has. */
export function retryBody(language: Pick<LanguageView, "code" | "depth">, review: boolean, version?: string) {
  return makeBody([language.code], review, language.depth, version);
}

/** "Translate the whole video": raises a ready voice language, checked before it is voiced. */
export function raiseBody(): { review: boolean; depth: LanguageDepth } {
  return { review: true, depth: "video" };
}

export const textDirection = (code: string): "rtl" | "ltr" => languageByCode(code)?.direction ?? "ltr";

/** Why nothing can be made from this video, or null when something can. */
export function blockedMessage(reason: LanguagesView["reason"]): string | null {
  switch (reason) {
    case "no_narration":
      return "This video has no narration, so there is nothing to translate.";
    case "no_timeline":
      return "Finish editing the video first.";
    case "version_not_ready":
      return "This version is not finished yet.";
    default:
      return null;
  }
}

export type ReviewMode = "review" | "revoice";

/**
 * What the script review is for: checking a script before it is voiced, or
 * rewording the lines of a ready language that ran long, voiced again free.
 * Neither once the English has changed — the server refuses edits then.
 */
export function reviewMode(language: Pick<LanguageView, "status" | "runsLong" | "outOfDate">): ReviewMode | null {
  if (language.outOfDate) return null;
  if (language.status === "review") return "review";
  if (language.status === "ready" && language.runsLong > 0) return "revoice";
  return null;
}

/** The lines that may be changed: all of them in review, only the long ones in a revoice. */
export function editableLineIds(
  language: Pick<LanguageView, "status" | "runsLong" | "outOfDate" | "lines">,
): Set<string> {
  const mode = reviewMode(language);
  if (mode === "review") return new Set(language.lines.map((line) => line.id));
  if (mode === "revoice") return new Set(language.lines.filter((line) => line.runsLong).map((line) => line.id));
  return new Set();
}

/** A line's length in its language's unit: words, or characters without spaces. */
export function lineCount(text: string, code: string): number {
  const language = languageByCode(code);
  return language ? measureLine(text, language) : text.split(/\s+/).filter(Boolean).length;
}

/** The unit a language's lines are counted in, for the counter. */
export function lineUnit(code: string): "words" | "characters" {
  return languageByCode(code)?.unit ?? "words";
}

/**
 * What discarding a language is called and what the in-place confirmation
 * says. A script in review that is an update of a made language (it still has
 * its files) discards only the update: the version it had stays. Anything else
 * is removed, and what it held against the balance is released.
 */
export function discardWording(language: Pick<LanguageView, "status" | "downloads" | "heldUsd">): {
  action: string;
  confirm: string;
} {
  if (language.status === "review" && language.downloads.video) {
    return { action: "Discard update", confirm: "Discard this new script? The version you have stays as it is." };
  }
  if (language.status === "review") {
    const released = language.heldUsd > 0 ? ` ${formatUsd(language.heldUsd)} is released and nothing is charged.` : "";
    return { action: "Discard", confirm: `Discard this script?${released}` };
  }
  return { action: "Discard", confirm: "Remove this language? Nothing was charged for it." };
}

/** What the launch player plays: the language's own film when it has one, else the English. */
export function launchVideoUrl(englishUrl: string, language: Pick<LanguageView, "downloads"> | null): string {
  const film = language?.downloads.video;
  return film ? film.replace(/\?download$/, "") : englishUrl;
}

export interface DownloadLink {
  kind: keyof LanguageView["downloads"];
  label: string;
  href: string;
}

const DOWNLOADS: { kind: keyof LanguageView["downloads"]; label: string }[] = [
  { kind: "video", label: "Video · MP4" },
  { kind: "vtt", label: "Subtitles .vtt" },
  { kind: "srt", label: ".srt" },
  { kind: "script", label: "Script" },
  { kind: "audio", label: "Voice" },
];

/**
 * A language's files, in the order the panel lists them. Only once it is
 * ready, and while a ready one is revoiced, because it keeps the files it had.
 */
export function downloadLinks(language: Pick<LanguageView, "status" | "downloads">): DownloadLink[] {
  if (language.status !== "ready" && language.status !== "revoicing") return [];
  return DOWNLOADS.flatMap(({ kind, label }) => {
    const href = language.downloads[kind];
    return href ? [{ kind, label, href }] : [];
  });
}

/**
 * What a new launch version is offered: the languages the version before it
 * was made in (any that did not fail), as far as this one can be made in them.
 */
export function previousOffer(
  previous: readonly Pick<LanguageView, "code" | "status">[],
  available: readonly AvailableLanguage[],
): AvailableLanguage[] {
  const wanted = new Set(previous.filter((one) => one.status !== "failed").map((one) => one.code));
  return available.filter((language) => wanted.has(language.code));
}

export function offerLabel(names: readonly string[], versionNumber: number, totalUsd: number, free = false): string {
  return `Make ${names.join(", ")} for v${versionNumber} too · ${priceLabel(totalUsd, free)}`;
}

/** A price as a button or card shows it: "Free" for a free-forever team, which is charged nothing. */
export function priceLabel(usd: number, free: boolean): string {
  return free ? "Free" : formatUsd(usd);
}

/** Whether two lists of codes hold the same languages, in any order. */
export function sameLanguages(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((code) => b.includes(code));
}

/**
 * Whether a language came out of a job since the last read: ready or failed
 * now, being made before. That is when the balance moved, charged or its hold
 * let go, and the chip in the bar should read it again.
 */
export function finishedSince(
  before: readonly Pick<LanguageView, "code" | "status">[] | null,
  after: readonly Pick<LanguageView, "code" | "status">[],
): boolean {
  if (!before) return false;
  return after.some((language) => {
    if (language.status !== "ready" && language.status !== "failed") return false;
    const was = before.find((one) => one.code === language.code);
    return was !== undefined && isInProgress(was.status);
  });
}

/**
 * What a launch player's switch offers: a language still to be checked, or one
 * with a film of its own. A ready one without a film has nothing to play.
 */
export function launchSwitchView(view: LanguagesView | null): LanguagesView | null {
  if (!view) return null;
  return {
    ...view,
    languages: view.languages.filter(
      (one) =>
        one.status === "review" || (one.status === "ready" && Boolean(one.downloads.video)) || isInProgress(one.status),
    ),
  };
}

/**
 * Where the old `/projects/:id/languages` page sends someone now: a launch
 * video's languages belong to its versions and live in its studio, at the
 * project's own page; a product demo's are the editor's Languages panel.
 */
export function languagesPageTarget(projectId: string, kind: "demo" | "launch"): string {
  return kind === "launch" ? `/projects/${projectId}?languages=open` : `/projects/${projectId}/editor?languages=open`;
}
